import { Typography } from "@mui/material";
import { DragEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useToast from "hooks/useToast";
import { ITeacherRecord } from "../../AddingIndividually";
import useFileBuilder, {
  IFile,
  IUploadedRow,
} from "../../../Content/useFileBuilder";
import { utils, writeFile } from "xlsx";
import { sheetColumns } from "../DownloadSample";
import { ErrorView } from "./ErrorView";
import { InitialView } from "./InitialView";
import { LoadedView } from "./LoadedView";

enum UploadState {
  NotLoaded = "NotLoaded",
  Loaded = "Loaded",
  Error = "Error",
}

export const columnsAr = ["اسم المدرس", "البريد الإلكترونيّ"];
export const columnsEn = ["Teacher Name", "Teacher Email"];
export interface ITeacherRecordWithError extends ITeacherRecord {
  errors?: string;
}
interface IDropZoneProps {
  errorData?: ITeacherRecordWithError[];
  showError: boolean;
  validateUploadedData(file: IFile, language: "ar" | "en"): void;
}

function DropZone({
  errorData,
  validateUploadedData,
  showError,
}: IDropZoneProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { file, buildFileSelector, setFile } = useFileBuilder();
  const [fileSelector, setFileSelector] = useState<HTMLInputElement>();
  const [fileValid, setFileValid] = useState(false);
  const [state, setState] = useState<UploadState>(UploadState.NotLoaded);

  useEffect(() => {
    const newfileSelector: HTMLInputElement = buildFileSelector();
    setFileSelector(newfileSelector);
  }, []);

  useEffect(() => {
    if (errorData?.length && showError) {
      setState(UploadState.Error);
    }
  }, [showError, errorData?.length]);

  useEffect(() => {
    if (file.data.length) readFileData();
  }, [file]);

  useEffect(() => {
    if (fileValid) {
      if (!(errorData?.length && showError)) setState(UploadState.Loaded);
    } else setState(UploadState.NotLoaded);
  }, [fileValid]);

  const removeHandler = () => {
    setFileValid(false);
    setFile({ data: [] });
  };

  const handleDownloadErrors = () => {
    if (!errorData || errorData.length === 0) {
      toast({
        label: t("Note"),
        message: t("No errors found, file not uploaded yet"),
        rtl: i18n.dir() === "rtl",
        type: "info",
        toastId: "invalid-value",
      });
      return;
    }
    const studentDetailsSheet = utils.json_to_sheet(
      errorData.map((rec) => ({
        [t(sheetColumns[0])]: rec.name,
        [t(sheetColumns[1])]: rec.email,
        [t("validation errors")]: rec.errors,
      }))
    );
    studentDetailsSheet["!cols"] = [{ wch: 20 }, { wch: 30 }, { wch: 50 }];
    const dwonloadWorkbook = utils.book_new();
    utils.book_append_sheet(
      dwonloadWorkbook,
      studentDetailsSheet,
      "teachers-details"
    );
    const errorFileName = "Errors_" + file.fileName;
    writeFile(dwonloadWorkbook, errorFileName);
  };

  const dropHandler = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (!files) return;
    if (files) {
      if (files.length > 1) {
        toast({
          label: t("Note"),
          message: t("Only one file is allowed"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "fileUpload",
        });
      }
      const extension = files[0].name.split(".").pop();
      if (extension && !["xlsx", "xls"].includes(extension)) {
        toast({
          label: t("Note"),
          message: t("Only excel files are currently supported."),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "fileUpload",
        });
        return;
      }
      if (fileSelector)
        fileSelector.onchange?.({ target: { files } } as unknown as Event);
    } else {
      toast({
        label: t("error"),
        message: t("No files"),
        rtl: i18n.dir() === "rtl",
        type: "error",
        toastId: "fileUpload",
      });
    }
  };

  const uploadHandler = () => {
    fileSelector?.click();
  };

  const rendderState = (value: UploadState) => {
    switch (value) {
      case UploadState.Loaded:
        return LoadedView({
          removeHandler,
          fileValid,
          file,
        });
      case UploadState.Error:
        return ErrorView({
          removeHandler,
          handleDownloadErrors,
          errorData,
          file,
        });
      case UploadState.NotLoaded:
      default:
        return InitialView({ dropHandler, uploadHandler });
    }
  };

  return (
    <Typography
      component="div"
      sx={{
        backgroundColor: "rgb(248 248 248)",
        borderRadius: "8px",
        height: "400px",
        width: "100%",
        margin: "20px 0",
        border: "2px dashed rgb(151 151 151)",
        fontSize: "14px",
      }}
    >
      {rendderState(state)}
    </Typography>
  );

  function readFileData() {
    let indexOfMaxColumns = 0;
    let rowOfMaxColumns: IUploadedRow = {};
    for (let row of file.data) {
      const rowColumnsCount = Object.keys(row).length;
      if (rowColumnsCount > indexOfMaxColumns) {
        indexOfMaxColumns = rowColumnsCount;
        rowOfMaxColumns = row;
      }
    }
    const sheetColumns = Object.keys(rowOfMaxColumns);
    const isSheetWithArSchema = columnsAr.some((column) =>
      sheetColumns.includes(column)
    );
    const isSheetWithEnSchema = columnsEn.some((column) =>
      sheetColumns.includes(column)
    );
    if (isSheetWithArSchema) {
      if (sheetColumns.includes(columnsAr[0])) {
        validateUploadedData(file, "ar");
        setFileValid(true);
      } else {
        toast({
          label: t("Note"),
          message: t("Teacher name column is missing"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "fileUpload",
        });
        setFileValid(false);
      }
    } else if (isSheetWithEnSchema) {
      if (sheetColumns.includes(columnsEn[0])) {
        validateUploadedData(file, "en");
        setFileValid(true);
      } else {
        toast({
          label: t("Note"),
          message: t("Teacher name column is missing"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "fileUpload",
        });
        setFileValid(false);
      }
    } else {
      toast({
        label: t("Note"),
        message: t("Allowed columns are missing"),
        rtl: i18n.dir() === "rtl",
        type: "error",
        toastId: "fileUpload",
      });
      setFileValid(false);
    }
  }
}

export default DropZone;
